import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Mitarbeiter {
  id: string; // UUID from database
  nummer: number;
  bmd_vertreter_id?: string | null;
  vorname: string;
  nachname: string;
  funktion: string;
  initialien: string;
  telefonnummer: string;
  email: string;
  status_id: number;
  is_techniker: boolean; // New field
  is_buero: boolean; // New field
  is_compadmin: boolean; // New field
  techniker_id: number | null; // Foreign key to mitarbeiter table
  mobile_pin: number;
  profile_image: string;
  account_id: number;
  full_name: string;
  display_name: string;
}

export interface TechnicianOption {
  id: number; // Numeric ID from database
  display_name: string;
  nummer: number;
  funktion: string;
}

export interface MitarbeiterTable {
  data: Mitarbeiter[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class MitarbeiterService {
  private apiUrl = `${environment.apiUrl}/mitarbeiter`;

  constructor(private http: HttpClient) {}

  getMitarbeiters(page: number = 1, limit: number = 10, search?: string, sortBy?: string, sortOrder?: string): Observable<MitarbeiterTable> {
    let url = `${this.apiUrl}?page=${page}&limit=${limit}`;
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    if (sortBy) {
      url += `&sortBy=${sortBy}`;
    }
    
    if (sortOrder) {
      url += `&sortOrder=${sortOrder}`;
    }
    
    return this.http.get<MitarbeiterTable>(url);
  }

  getMitarbeiter(id: string): Observable<{data: Mitarbeiter}> {
    return this.http.get<{data: Mitarbeiter}>(`${this.apiUrl}/${id}`);
  }

  createMitarbeiter(mitarbeiter: Partial<Mitarbeiter>): Observable<{data: Mitarbeiter}> {
    return this.http.post<{data: Mitarbeiter}>(this.apiUrl, mitarbeiter);
  }

  updateMitarbeiter(id: string, mitarbeiter: Partial<Mitarbeiter>): Observable<{data: Mitarbeiter}> {
    return this.http.put<{data: Mitarbeiter}>(`${this.apiUrl}/${id}`, mitarbeiter);
  }

  deleteMitarbeiter(id: string): Observable<{message: string}> {
    return this.http.delete<{message: string}>(`${this.apiUrl}/${id}`);
  }

  // Helper method to get active technicians
  getActiveTechnicians(): Observable<MitarbeiterTable> {
    return this.getMitarbeiters(1, 50, '', 'nachname', 'asc');
  }

  // Get all active technicians for dropdown selection
  getTechnicians(includeInactiveForRelationships: boolean = false): Observable<{data: TechnicianOption[]}> {
    const params: any = {};
    if (includeInactiveForRelationships) {
      params.include_inactive_for_relationships = 'true';
    }
    return this.http.get<{data: TechnicianOption[]}>(`${this.apiUrl}/technicians`, { params });
  }

  // Get technicians only (filtered by is_techniker = true)
  getTechnikerList(page: number = 1, limit: number = 10, search?: string): Observable<MitarbeiterTable> {
    let url = `${this.apiUrl}/techniker?page=${page}&limit=${limit}`;

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    return this.http.get<MitarbeiterTable>(url);
  }

  // Helper method to search by employee number
  searchByNummer(nummer: number): Observable<MitarbeiterTable> {
    return this.getMitarbeiters(1, 10, nummer.toString(), 'nummer', 'asc');
  }
} 